import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { TodoNoteCategoryDraft, TodoNoteItemView } from '../../types/todoNote';
import { isTodoNoteFavorite, TODO_POSTIT_FAVORITE_GROUP_KEY } from '../../utils/todoNoteFavorite';
import { joinTodoMemo } from './todoNoteRowShared';
import TodoPostitCardActions from './TodoPostitCardActions';
import TodoChecklistText from './TodoChecklistText';
import {
  buildTodoPostitTiles,
  formatTodoPostitDate,
  layoutTodoPostitGrid,
  TODO_POSTIT_CARD_MIN_HEIGHT,
  TODO_POSTIT_STACK_LAYER_MAX,
  todoPostitCategoryRankById,
  todoPostitGridMetrics,
  todoPostitHeightOf,
  todoPostitStackExtra,
} from '../../utils/todoNotePostitLayout';

const WORLD_PAD_PX = 48;

type TodoNotePostitBoardProps = {
  boardId: string;
  items: TodoNoteItemView[];
  categories: TodoNoteCategoryDraft[];
  highlightCategoryId?: string | null;
  groupByCategory?: boolean;
  onDuplicate: (itemId: string) => void;
  onToggleFavorite: (itemId: string) => void;
  onOpen: (itemId: string) => void;
  todayYmd: string;
  fullPage?: boolean;
};

const TodoNotePostitBoard: React.FC<TodoNotePostitBoardProps> = ({
  boardId,
  items,
  categories,
  highlightCategoryId = null,
  groupByCategory = true,
  onDuplicate,
  onToggleFavorite,
  onOpen,
  todayYmd,
  fullPage = false,
}) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const cardElsRef = useRef<Map<string, HTMLElement>>(new Map());
  const [boardWidth, setBoardWidth] = useState(360);
  const [heightById, setHeightById] = useState<Record<string, number>>({});

  const categoryRankById = useMemo(() => todoPostitCategoryRankById(categories), [categories]);
  const tiles = useMemo(
    () => buildTodoPostitTiles(items, todayYmd, groupByCategory ? categoryRankById : undefined),
    [categoryRankById, groupByCategory, items, todayYmd]
  );
  const itemIds = useMemo(() => tiles.map((tile) => tile.id), [tiles]);
  const groupKeyById = useMemo(() => {
    if (!groupByCategory) return undefined;
    const map: Record<string, string> = {};
    for (const tile of tiles) {
      map[tile.id] = isTodoNoteFavorite(tile.item)
        ? TODO_POSTIT_FAVORITE_GROUP_KEY
        : (tile.item.category_id ?? '').trim();
    }
    return map;
  }, [groupByCategory, tiles]);
  const tileById = useMemo(() => {
    const map = new Map<string, (typeof tiles)[number]>();
    tiles.forEach((tile) => map.set(tile.id, tile));
    return map;
  }, [tiles]);
  const metrics = useMemo(() => todoPostitGridMetrics(boardWidth), [boardWidth]);
  const categoryColorById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of categories) map.set(c.id, c.color);
    return map;
  }, [categories]);
  const positions = useMemo(
    () => layoutTodoPostitGrid(itemIds, boardWidth, { heightById, groupKeyById }),
    [boardWidth, groupKeyById, heightById, itemIds]
  );

  const readCardHeights = useCallback(() => {
    const next: Record<string, number> = {};
    cardElsRef.current.forEach((el, id) => {
      const tile = tileById.get(id);
      const extra = tile ? todoPostitStackExtra(tile.stackCount, tile.isRoutineStack) : 0;
      next[id] = Math.max(TODO_POSTIT_CARD_MIN_HEIGHT, Math.round(el.offsetHeight) + extra);
    });
    return next;
  }, [tileById]);

  const bindCardEl = (id: string) => (el: HTMLElement | null) => {
    if (!el) {
      cardElsRef.current.delete(id);
      return;
    }
    cardElsRef.current.set(id, el);
  };

  useEffect(() => {
    const viewport = viewportRef.current;
    const width = viewport?.clientWidth || 360;
    setBoardWidth(width);
  }, [boardId]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number' && w > 0) setBoardWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    const next = readCardHeights();
    setHeightById((prev) => {
      const ids = Object.keys(next);
      const same =
        ids.length === Object.keys(prev).length && ids.every((id) => prev[id] === next[id]);
      return same ? prev : next;
    });
  }, [boardWidth, itemIds, readCardHeights, tiles]);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      const next = readCardHeights();
      setHeightById((prev) => {
        const ids = Object.keys(next);
        const same =
          ids.length === Object.keys(prev).length && ids.every((id) => prev[id] === next[id]);
        return same ? prev : next;
      });
    });
    cardElsRef.current.forEach((el) => ro.observe(el));
    return () => ro.disconnect();
  }, [itemIds, readCardHeights]);

  const worldSize = useMemo(() => {
    let maxY = metrics.pad + TODO_POSTIT_CARD_MIN_HEIGHT + WORLD_PAD_PX;
    for (const id of itemIds) {
      const pos = positions[id];
      if (!pos) continue;
      maxY = Math.max(maxY, pos.y + todoPostitHeightOf(id, heightById) + WORLD_PAD_PX);
    }
    return { width: Math.max(boardWidth, 1), height: maxY };
  }, [boardWidth, heightById, itemIds, metrics.pad, positions]);

  return (
    <section
      className={`cp-todo-note-postit-board${fullPage ? ' is-page' : ''}${
        highlightCategoryId ? ' is-category-focus' : ''
      }`}
      aria-label="포스트잇"
    >
      {fullPage ? null : (
        <div className="cp-todo-note-postit-head">
          <h3 className="cp-todo-note-postit-title">포스트잇</h3>
        </div>
      )}
      <div ref={viewportRef} className="cp-todo-note-postit-viewport">
        <div
          className="cp-todo-note-postit-world"
          style={{ width: worldSize.width, height: worldSize.height }}
        >
          {tiles.length === 0 ? (
            <p className="cp-todo-note-muted cp-todo-note-postit-empty">완료되지 않은 메모가 없습니다.</p>
          ) : (
            tiles.map((tile) => {
              const it = tile.item;
              const pos = positions[tile.id] ?? { x: metrics.pad, y: metrics.pad };
              const catColor = it.category_id ? categoryColorById.get(it.category_id) : undefined;
              const memo = joinTodoMemo(it.title, it.body).trim();
              const misc = (it.misc ?? '').trim();
              const canCopy = !tile.isRoutineStack && !it.is_routine && !it.routineParentId;
              const isDimmed = Boolean(highlightCategoryId) && it.category_id !== highlightCategoryId;
              const isFavorite = isTodoNoteFavorite(it);
              const isDueToday =
                it.due_kind === 'day' && String(it.due_date ?? '').trim() === todayYmd;
              const stackLayers = tile.isRoutineStack
                ? Math.min(TODO_POSTIT_STACK_LAYER_MAX, Math.max(0, tile.stackCount - 1))
                : 0;
              return (
                <article
                  key={tile.id}
                  ref={bindCardEl(tile.id)}
                  className={
                    'cp-todo-note-postit-card' +
                    (tile.isRoutineStack ? ' is-routine-stack' : '') +
                    (isDimmed ? ' is-dimmed' : '') +
                    (!isDimmed && highlightCategoryId ? ' is-highlighted' : '') +
                    (isDueToday ? ' is-due-today' : '') +
                    (isFavorite ? ' is-favorite' : '')
                  }
                  style={{
                    left: pos.x,
                    top: pos.y,
                    width: metrics.cellWidth,
                    ['--todo-postit-cat' as string]: catColor?.trim() || '#e7c45a',
                  }}
                  onClick={() => onOpen(it.id)}
                >
                  {Array.from({ length: stackLayers }, (_, i) => {
                    const layer = stackLayers - i;
                    return (
                      <span
                        key={layer}
                        className={
                          'cp-todo-note-postit-stack-layer' + (layer === 1 ? ' is-fan-a' : ' is-fan-b')
                        }
                        aria-hidden
                      />
                    );
                  })}
                  {stackLayers > 0 ? <span className="cp-todo-note-postit-face" aria-hidden /> : null}
                  <header className="cp-todo-note-postit-card-head">
                    <span className="cp-todo-note-postit-date">
                      {formatTodoPostitDate(it.due_kind, it.due_date)}
                    </span>
                    <TodoPostitCardActions
                      canCopy={canCopy}
                      isFavorite={isFavorite}
                      onDuplicate={canCopy ? () => onDuplicate(it.id) : undefined}
                      onToggleFavorite={() =>
                        onToggleFavorite(tile.isRoutineStack ? tile.id : it.id)
                      }
                    />
                  </header>
                  <div className="cp-todo-note-postit-memo">
                    {memo ? <TodoChecklistText text={memo} /> : '메모 없음'}
                  </div>
                  {misc ? <div className="cp-todo-note-postit-misc">{misc}</div> : null}
                </article>
              );
            })
          )}
        </div>
      </div>
    </section>
  );
};

export default TodoNotePostitBoard;
